import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Clapperboard } from 'lucide-react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { InternalServiceError, ServiceUnreachableError } from '@zyx/shared';
import { AppShell } from '@/components/layout/app-shell';
import { SetupScreen } from '@/components/setup/setup-screen';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ConnectionProvider, useConnection } from '@/hooks/use-connection';
import { ThemeProvider } from '@/hooks/use-theme';
import { BookmarksPage } from '@/pages/bookmarks-page';
import { BrowsePage } from '@/pages/browse-page';
import { DownloadsPage } from '@/pages/downloads-page';
import { CollectionsPage } from '@/pages/collections-page';
import { HomePage } from '@/pages/home-page';
import { IndexingPage } from '@/pages/indexing-page';
import { JobPage } from '@/pages/job-page';
import { FavouritesPage, HiddenPage, HistoryPage, WatchlistPage } from '@/pages/library-pages';
import { ListPage, ListsPage } from '@/pages/lists-page';
import { MoviePage } from '@/pages/movie-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { PersonPage } from '@/pages/person-page';
import { TermPage } from '@/pages/term-page';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // A 400, 401 or 404 will not become true by asking again.
        const worthRetrying = error instanceof ServiceUnreachableError || error instanceof InternalServiceError;
        return worthRetrying && failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** Every theme is dark, and the toasts take its colours through the tokens. */
function ThemedToaster() {
  return <Toaster theme="dark" position="bottom-right" richColors closeButton />;
}

function ConnectingSplash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-muted-foreground">
      <Clapperboard className="size-6 animate-pulse text-primary" />
      <p className="text-sm">Connecting…</p>
    </div>
  );
}

/** A gate rather than a route: every page needs a client to call. */
function ConnectedApp() {
  const { state } = useConnection();
  if (state.status === 'probing') {
    return <ConnectingSplash />;
  }
  if (state.status === 'setup') {
    return <SetupScreen initial={state.candidate} outcome={state.outcome} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          <Route path="browse" element={<BrowsePage />} />
          <Route path="movies/:code" element={<MoviePage />} />
          <Route path="people/:personId" element={<PersonPage />} />
          <Route path="terms/:termId" element={<TermPage />} />
          <Route path="collections" element={<CollectionsPage />} />
          <Route path="collections/:kind" element={<CollectionsPage />} />
          <Route path="watchlist" element={<WatchlistPage />} />
          <Route path="favourites" element={<FavouritesPage />} />
          <Route path="hidden" element={<HiddenPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="lists" element={<ListsPage />} />
          <Route path="lists/:listId" element={<ListPage />} />
          <Route path="bookmarks" element={<BookmarksPage />} />
          <Route path="downloads" element={<DownloadsPage />} />
          <Route path="indexing" element={<IndexingPage />} />
          <Route path="indexing/:tab" element={<IndexingPage />} />
          <Route path="indexing/jobs/:jobId" element={<JobPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ConnectionProvider>
          <TooltipProvider delayDuration={300}>
            <ConnectedApp />
            <ThemedToaster />
          </TooltipProvider>
        </ConnectionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
